import { observable, computed } from 'mobx';
import { CXBaseBlock } from '~/components/ClientExperience/OneFlow/models/cx-base-block.model';
import { ISignature, SignatureTypes } from '~/types/model.types';
import {
    ISignatureBlockResponse,
    ISignatureVariableValue
} from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { ISignatureValue } from '~/components/ClientExperience/OneFlow/FlowContent/CXContractBlock/CXSignature.types';
import type { CXContractBlock } from '~/components/ClientExperience/OneFlow/models/cx-contract-block.model';
import { Variable } from '~/models/variable.model';
import type { FlowRevision } from '~/models/flow-revision.model';

export class CXSignatureBlock extends CXBaseBlock {
    type: 'signature_block';
    @observable signature: ISignature;

    constructor(
        block: ISignatureBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.signature = block.signature || {
            signee_id: '',
            signee_name: '',
            signee_email: '',
            signee_type: 'client',
            required: false,
            signee_details: null
        };
        this.connected_variable_id = block.connected_variable_id;
    }

    get connectedVariable(): Variable<ISignatureVariableValue> {
        return super.connectedVariable;
    }

    defaultSignatureData(): Partial<ISignatureVariableValue> {
        const {
            flowViewUiStore: { selectedFlow }
        } = this.rootStore;

        // we only prefill name for vendor
        if (this.signature.signee_type === 'client') {
            return null;
        }

        const signee = selectedFlow.flowVendorsList.find(
            vendor => vendor._id === this.signature.signee_id
        );

        return {
            signature: signee?.full_name,
            signature_type: SignatureTypes.TYPE
        };
    }

    @computed
    get contract() {
        if (this.parent.parent.type === 'contract_block') {
            return this.parent.parent as CXContractBlock;
        }
        return null;
    }

    @computed
    get isSigned() {
        return !!(this.connectedVariable?.value as ISignatureValue)?.signature;
    }

    @computed
    get missingRequiredSignature() {
        return this.signature.required && !this.isSigned;
    }

    @computed
    get variableValue() {
        return this.rootStore.flowStore.variables[this.connected_variable_id]
            ?.value;
    }

    @computed
    get signeeId() {
        return this.signature.signee_id;
    }

    @computed
    get isAssigned() {
        return !!this.signature.signee_id;
    }

    @computed
    get signeeType() {
        return this.signature.signee_type;
    }

    @computed
    get signeeName() {
        return this.signature.signee_name;
    }

    @computed
    get signeeDetails() {
        return this.signature.signee_details;
    }

    @computed
    get signeeEmail() {
        return this.signature.signee_email;
    }

    @computed
    get isRequired() {
        return this.signature.required;
    }

    @computed
    get variableRawValue() {
        // todo delete this when getting rid of raw_value completely
        return this.rootStore.flowStore.variables[this.connected_variable_id]
            ?.value;
    }
}
